export type RuntimeConfig = {
  apiBaseUrl?: string;
  appVersion?: string;
};

const defaultRuntimeConfig: RuntimeConfig = {
  apiBaseUrl: "",
};

let cachedConfig: RuntimeConfig = defaultRuntimeConfig;

const normalizeBaseUrl = (value?: string) => {
  if (!value) {
    return "";
  }
  return value.replace(/\/+$/, "");
};

export const loadRuntimeConfig = async (): Promise<RuntimeConfig> => {
  if (window.__ML_RUNTIME_CONFIG__) {
    cachedConfig = {
      ...defaultRuntimeConfig,
      ...window.__ML_RUNTIME_CONFIG__,
    };
    cachedConfig.apiBaseUrl = normalizeBaseUrl(cachedConfig.apiBaseUrl);
    return cachedConfig;
  }

  try {
    const configUrl = new URL("config.json", `${window.location.origin}/`);
    const response = await fetch(configUrl, { cache: "no-store" });
    if (!response.ok) {
      throw new Error("Failed to load runtime config.");
    }
    const data = (await response.json()) as RuntimeConfig;
    cachedConfig = {
      ...defaultRuntimeConfig,
      ...data,
      apiBaseUrl: normalizeBaseUrl(data.apiBaseUrl),
    };
  } catch {
    cachedConfig = defaultRuntimeConfig;
  }

  window.__ML_RUNTIME_CONFIG__ = cachedConfig;
  return cachedConfig;
};

export const getRuntimeConfig = (): RuntimeConfig => {
  return window.__ML_RUNTIME_CONFIG__ ?? cachedConfig;
};

export const getApiBaseUrl = (): string => {
  return normalizeBaseUrl(getRuntimeConfig().apiBaseUrl);
};

export const buildApiUrl = (path: string): string => {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    return path;
  }
  return `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
};

export const resolveApiUrl = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return buildApiUrl(value);
};

export const getAppVersion = (): string | undefined => {
  const runtimeVersion = getRuntimeConfig().appVersion;
  if (runtimeVersion?.trim()) {
    return runtimeVersion.trim();
  }

  const envVersion = process.env.REACT_APP_VERSION;
  if (envVersion?.trim()) {
    return envVersion.trim();
  }

  const commitSha = process.env.REACT_APP_GIT_SHA;
  if (commitSha?.trim()) {
    return commitSha.trim();
  }

  return undefined;
};
